/**
 * Analytics Utility - Dual Analytics Integration
 *
 * Supports both Plausible Analytics (privacy-friendly) and Google Analytics 4 (detailed insights).
 * Automatically integrates with both platforms if configured.
 */

import ReactGA from 'react-ga4';

// Initialize GA4 (call this in App.js on mount)
export const initGA = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (measurementId) {
    try {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          debug_mode: process.env.NODE_ENV === 'development',
        },
      });
      console.log('✅ Google Analytics 4 initialized');
    } catch (error) {
      console.error('❌ GA4 initialization failed:', error);
    }
  } else {
    console.log('ℹ️ GA4 not configured - using Plausible only');
  }
};

// Track page views (call this on route changes)
export const trackPageView = (path, title) => {
  // GA4 page view
  if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title,
    });
  }
  // Plausible handles page views automatically
};

/**
 * Track custom events with both Plausible and GA4
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties (optional)
 */
export const trackEvent = (eventName, properties = {}) => {
  // Track with Plausible
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible(eventName, { props: properties });
  }

  // Track with GA4
  if (process.env.REACT_APP_GA_MEASUREMENT_ID) {
    ReactGA.event(eventName.toLowerCase().replace(/ /g, '_'), properties);
  }
};

// Convenience functions for common events

export const trackSignup = (method) => {
  trackEvent('Signup', { method });
};

export const trackLogin = (method) => {
  trackEvent('Login', { method });
};

export const trackPromptCreated = (category) => {
  trackEvent('Prompt Created', { category });
};

export const trackPromptViewed = (promptId) => {
  trackEvent('Prompt Viewed', { promptId: String(promptId) });
};

export const trackRatingSubmitted = (rating) => {
  trackEvent('Rating Submitted', { rating: String(rating) });
};

export const trackShare = (platform) => {
  trackEvent('Share Clicked', { platform });
};

export const trackBadgeUnlocked = (badgeName) => {
  trackEvent('Badge Unlocked', { badge: badgeName });
};

export const trackSearchPerformed = (query) => {
  trackEvent('Search', { query });
};

export const trackCollectionCreated = () => {
  trackEvent('Collection Created');
};

export const trackFollow = (action) => {
  // action: 'follow' or 'unfollow'
  trackEvent('Follow Action', { action });
};

export const trackCommentAdded = () => {
  trackEvent('Comment Added');
};

export const trackPromptForked = () => {
  trackEvent('Prompt Forked');
};

export const trackTokenAnalyzerUsed = (feature) => {
  // feature: 'count' or 'optimize'
  trackEvent('Token Analyzer Used', { feature });
};

export const trackUpgrade = (tier) => {
  // For future monetization
  trackEvent('Upgrade', { tier });
};

export const trackOnboardingCompleted = (step) => {
  trackEvent('Onboarding', { step });
};

const analytics = {
  initGA,
  trackPageView,
  trackEvent,
  trackSignup,
  trackLogin,
  trackPromptCreated,
  trackPromptViewed,
  trackRatingSubmitted,
  trackShare,
  trackBadgeUnlocked,
  trackSearchPerformed,
  trackCollectionCreated,
  trackFollow,
  trackCommentAdded,
  trackPromptForked,
  trackTokenAnalyzerUsed,
  trackUpgrade,
  trackOnboardingCompleted,
};

export default analytics;
